import { type PropsWithChildren } from 'react';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { capitalize } from 'es-toolkit/string';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import { useOpenWindow, useViewport } from '@/hooks';
import { BoltFillIcon, ChevronRightIcon } from '@/icons';
import { planStartAlertStorage } from '@/module/plan/storage';
import { type PlanStartModalProps } from '@/module/plan/types';
import { Conditional } from '@/shared/googleClassroom';
import { type PlanType } from '@/types';
import { type CommonTranslationKeys } from '@/types/translation';
import { Button } from '@/ui_v3';
import { ResponsiveDialog } from '@/ui_v3/components/dialog';
import { cn } from '@/utils';
import { formatLocalizedDate } from '@/utils/dateFormat';
const IMAGES = {
  BASIC: {
    normal: '/assets/basic_start_pc.webp',
    mobile: '/assets/basic_start_mobile.webp'
  },
  PRO: {
    normal: '/assets/pro_start_pc.webp',
    mobile: '/assets/pro_start_mobile.webp'
  }
};

/**
 * 현재 페이지와 다른 경우에만 replace를 실행하는 안전한 네비게이션 함수
 */
const safeReplace = (router: ReturnType<typeof useRouter>, path: string) => {
  if (router.pathname === path) return;
  void router.replace(path);
};
export const PlanStartModal = ({
  planType,
  endDate,
  onClose,
  isOwner,
  isPaid,
  eventType
}: PlanStartModalProps) => {
  const router = useRouter();
  const {
    isMobile
  } = useViewport();
  const openWindow = useOpenWindow();
  const {
    t
  } = useTranslation(['common']);
  const isEvent = eventType === 'TWO_WEEKS_FREE';
  const title: CommonTranslationKeys = isEvent ? 'build.guide.notifyTrialPlan' : 'modal.planStarted';
  const description: CommonTranslationKeys = isEvent ? 'build.guide.welcomeQuiz' : 'plan.proPlan.description2';
  const formattedDate = formatLocalizedDate(endDate);
  const handleClose = () => {
    planStartAlertStorage.markAsShown();
    onClose?.();
  };
  const leftActionButton = isOwner && isPaid ? {
    label: t('modal.inviteMember'),
    onClick: () => {
      safeReplace(router, '/profile/plan');
    }
  } : {
    label: t('build.guide.explore'),
    onClick: () => {
      safeReplace(router, '/public');
    }
  };
  const images = planType === 'BASIC' ? IMAGES.BASIC : IMAGES.PRO;
  return <ResponsiveDialog size="lg" onClose={() => {
    //
  }} data-sentry-element="ResponsiveDialog" data-sentry-component="PlanStartModal" data-sentry-source-file="PlanStartModal.tsx">
      <main className="flex h-full flex-col gap-md overflow-y-auto p-lg px-md mobile-lg:flex-row mobile-lg:px-lg">
        <div className="flex flex-col gap-sm px-[6px] mobile-lg:gap-xs mobile-lg:p-md mobile-lg:pr-0">
          <div className="flex items-center gap-sm mobile-lg:flex-col mobile-lg:items-start mobile-lg:gap-lg">
            <PlanIcon planType={planType} data-sentry-element="PlanIcon" data-sentry-source-file="PlanStartModal.tsx" />
            <h1 className="text-heading-xl-extrabold">
              <Trans t={t} i18nKey={title} components={{
              Br: <br className="hidden mobile-lg:block" />
            }} values={{
              planName: capitalize(planType)
            }} data-sentry-element="Trans" data-sentry-source-file="PlanStartModal.tsx" />
            </h1>
          </div>
          <p className="text-body-lg-medium">
            <Trans t={t} i18nKey={description} components={{
            Strong: <strong className="text-body-lg-bold" />
          }} values={{
            endDate: formattedDate
          }} data-sentry-element="Trans" data-sentry-source-file="PlanStartModal.tsx" />
          </p>
        </div>
        <div className="min-h-[375px] w-full shrink-0 mobile-lg:min-h-[500px] mobile-lg:max-w-[400px]">
          <img className="size-full" src={isMobile ? images.mobile : images.normal} alt="Plan Start" />
        </div>
      </main>

      <footer className="flex shrink-0 flex-col items-center gap-md border border-t border-alternative px-md pb-lg pt-md shadow-lg mobile-lg:px-lg">
        <QuestionLink onClick={() => {
        handleClose();
        openWindow('/pricing');
      }} data-sentry-element="QuestionLink" data-sentry-source-file="PlanStartModal.tsx">
          {t('modal.whatIsPremiumPlan')}
        </QuestionLink>
        <div className={'flex w-full gap-xs'}>
          <Button size="lg" intent={'neutral'} className="min-w-[170px] shrink-0 mobile-lg:min-w-[264px]" onClick={() => {
          leftActionButton.onClick();
          handleClose();
        }} data-sentry-element="Button" data-sentry-source-file="PlanStartModal.tsx">
            {leftActionButton.label}
          </Button>
          <Button size="lg" className={cn('w-full', planType === 'BASIC' && 'bg-gradient-mint-green')} intent={'gradient'} onClick={() => {
          safeReplace(router, '/build');
          handleClose();
        }} data-sentry-element="Button" data-sentry-source-file="PlanStartModal.tsx">
            <BoltFillIcon size={'md'} fill={'#FFF'} data-sentry-element="BoltFillIcon" data-sentry-source-file="PlanStartModal.tsx" />
            {t('build.guide.makeQuiz')}
          </Button>
        </div>
      </footer>
    </ResponsiveDialog>;
};
const PlanIcon = ({
  planType
}: {
  planType: PlanType;
}) => {
  const {
    isMobile
  } = useViewport();
  return <div className={cn('mobile-lg:size-[42px] size-[32px] flex justify-center items-center rounded-sm', planType === 'PRO' ? 'bg-gradient-violet-blue' : 'bg-gradient-mint-green')} data-sentry-component="PlanIcon" data-sentry-source-file="PlanStartModal.tsx">
      <BoltFillIcon size={isMobile ? 'md' : 'lg'} className="text-white" data-sentry-element="BoltFillIcon" data-sentry-source-file="PlanStartModal.tsx" />
    </div>;
};
const QuestionLink = ({
  children,
  onClick
}: PropsWithChildren<{
  onClick?: () => void;
}>) => {
  return <Conditional inGoogleClassroom={null} data-sentry-element="Conditional" data-sentry-component="QuestionLink" data-sentry-source-file="PlanStartModal.tsx">
      <span className={cn('flex justify-center items-center cursor-pointer text-strong text-body-lg-bold')} onClick={onClick}>
        {children}
        <ChevronRightIcon size="sm" fill={ICON_COLOR.strong} data-sentry-element="ChevronRightIcon" data-sentry-source-file="PlanStartModal.tsx" />
      </span>
    </Conditional>;
};